import { useRevalidator } from 'react-router';

import { useUserLoggedIn } from '~/modules/user/hook/use-user';

import { cn } from '@rocco/utils/cn';
import { ReactComponent as Logomark } from '@rocco/icons/svg/logo-new-black.svg';
import { CONTENT_CAP } from '~/ui-rocco/pages/detail-shared';
import { AuthPanel } from '~/modules-rocco/user/component/auth-modal/AuthPanel';
import joinPortrait from '../images/join-portrait.webp';
import joinExterior from '../images/join-exterior.webp';

/**
 * The band's right column: the shared auth panel, the same one the auth modal
 * and `/login` render — email-first with account detection, passkey login, the
 * passwordless code step and forgot-password. It brings its own headline and
 * subcopy from the intent registry, which is where this band's wording came
 * from in the first place.
 *
 * `skipAuthRedirect` because the reader is mid-page: the post-auth destination
 * would take them off the product or project they were reading. Revalidating
 * instead re-runs the route's loaders, so the page comes back as a member's —
 * saved state, pricing, the lot — without a reload or a scroll jump.
 */
const BandAuthPanel = () => {
    const { revalidate } = useRevalidator();
    return (
        <AuthPanel
            authSource="signUpBand"
            skipAuthRedirect
            onAuthSuccess={() => {
                void revalidate();
            }}
        />
    );
};

/**
 * Every measurement here is a Figma value at the 1920 frame (node 5:100),
 * expressed in rem — the root font-size is vw-anchored to that frame, so the
 * whole band scales with the viewport rather than needing a breakpoint per
 * width. That anchoring starts at lg, so lg is also where the designed
 * composition starts; below it the pitch and the panel stack and the photos
 * drop out, since the design has no frame narrower than 1920 and the
 * overlapping photos have nowhere to go.
 */
export const JoinArchiProSection = () => {
    const { isLoggedIn } = useUserLoggedIn();

    // The whole band asks the reader to create an account, so it has nothing
    // to offer a member. Callers drop the air around it too; this is the guard
    // that holds if one of them forgets.
    if (isLoggedIn) return null;

    return (
        <section className="bg-white" aria-labelledby="join-archipro-heading">
            <div className={cn(CONTENT_CAP, 'px-4 lg:px-0')}>
                {/* Desktop: the designed composition, laid out against the frame's
                own 1002px height so the photos can overlap as drawn. Every
                measurement here is a rem off the 1920 frame, and the root
                font-size is only anchored to that frame from lg, so this
                branch starts where the anchoring does rather than at md. */}
                <div className="relative hidden h-[62.625rem] w-full lg:block">
                    <Logomark
                        aria-hidden="true"
                        className="absolute top-[4.65rem] left-[2.807rem] h-[10.239rem] w-[13.543rem]"
                    />

                    {/* The page's large-text style (the product title and the
                    "More in …" heading): 3.9rem, 1.08 leading, -0.125rem
                    tracking, Roman rather than Medium. */}
                    <h2
                        id="join-archipro-heading"
                        className="absolute top-[30.75rem] left-[2.493rem] m-0 w-[26.938rem] font-sans text-[3.9rem] leading-[1.08] font-normal tracking-[-0.125rem] text-[#181818]"
                    >
                        Get instant access to all ArchiPro features by creating
                        a free account
                    </h2>

                    <a
                        href="/"
                        className="absolute top-[56.906rem] left-[2.493rem] font-sans text-[1.125rem] font-medium tracking-[-0.0225rem] text-[#656565] underline underline-offset-2 transition-colors hover:text-[#181818]"
                    >
                        What is ArchiPro?
                    </a>

                    {/* The two photos overlap: the exterior sits lower and to the
                    left, in front of the portrait's bottom corner. */}
                    <img
                        src={joinPortrait}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                        className="absolute top-[9.051rem] left-[41.675rem] h-[37.149rem] w-[29.309rem] object-cover object-bottom"
                    />
                    <img
                        src={joinExterior}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                        className="absolute top-[33.901rem] left-[33.437rem] h-[20.881rem] w-[16.474rem] object-cover"
                    />

                    {/* Full-height hairline separating the pitch from the panel. */}
                    <div
                        aria-hidden="true"
                        className="absolute top-1/2 left-[77.754rem] h-[56.336rem] w-px -translate-y-1/2 bg-[#d9d9d9]"
                    />

                    <div className="absolute top-[12.391rem] left-[82.675rem] w-[30.492rem]">
                        <BandAuthPanel />
                    </div>
                </div>

                {/* Below lg the composition has nowhere to go, so the pitch and the
                panel simply stack and the photos drop out. */}
                <div className="flex flex-col gap-10 py-12 lg:hidden">
                    <Logomark aria-hidden="true" className="h-20 w-auto" />
                    <h2 className="m-0 font-sans text-[2rem] leading-[1.08] font-normal tracking-[-0.02em] text-[#181818]">
                        Get instant access to all ArchiPro features by creating
                        a free account
                    </h2>
                    <BandAuthPanel />
                    <a
                        href="/"
                        className="w-fit font-sans text-sm font-medium text-[#656565] underline underline-offset-2"
                    >
                        What is ArchiPro?
                    </a>
                </div>
            </div>
        </section>
    );
};
